export function stopCamera(stream?: MediaStream | null): void {
  stream?.getTracks().forEach(track => track.stop());
}

/** An ignored permission dialog must not leave the UI blocked or a late stream running. */
export function openChatCamera(
  devices: Pick<MediaDevices, "getUserMedia">,
  signal: AbortSignal,
  timeoutMs = 20_000
): Promise<MediaStream> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cleanup = () => { clearTimeout(timer); signal.removeEventListener("abort", cancel); };
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true; cleanup(); reject(error);
    };
    const cancel = () => fail(new DOMException("Cámara cancelada.", "AbortError"));
    if (signal.aborted) { cancel(); return; }
    signal.addEventListener("abort", cancel, { once: true });
    timer = setTimeout(() => fail(new DOMException("No se recibió permiso de cámara.", "TimeoutError")), timeoutMs);
    try {
      void devices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }
      }).then(stream => {
        if (settled || signal.aborted) { stopCamera(stream); return; }
        if (!stream.getVideoTracks().some(track => track.readyState === "live")) {
          stopCamera(stream); fail(new DOMException("No hay cámara disponible.", "NotFoundError")); return;
        }
        settled = true; cleanup(); resolve(stream);
      }, fail);
    } catch (error) { fail(error); }
  });
}

export function cameraErrorMessage(error: unknown): string {
  const name = error && typeof error === "object" && "name" in error ? String(error.name) : "";
  if (name === "NotAllowedError" || name === "SecurityError") return "No se ha concedido permiso para la cámara. Puedes permitirlo en los ajustes o adjuntar una foto.";
  if (name === "NotFoundError") return "No se encontró una cámara. Puedes adjuntar una foto desde este dispositivo.";
  if (name === "NotReadableError") return "La cámara está ocupada o no puede abrirse. Cierra la otra aplicación o adjunta una foto.";
  if (name === "TimeoutError") return "No se recibió permiso a tiempo. Puedes intentarlo de nuevo o adjuntar una foto.";
  return "No se pudo abrir la cámara. Puedes usar la cámara del dispositivo o adjuntar una foto.";
}
